import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import itemsStore from '../../store/itemsStore';
import { SCREENS } from 'src/constants/screens';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { history } from 'src/services/history';
import { TBooleanInNumber } from 'src/types/main';
import { IProductV4 } from 'src/types/product';
import { formatPrice } from 'src/utils/formatter';
import { booleanNumberReverse, getOrderTypeIconsNames, getTranslate } from 'src/utils/helpers';
import { confirmDelete } from 'src/utils/modalConfirmations';
import { TOOLTIP_DURATION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import Image from 'src/view/components/Image/Image';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import OrderTypes from 'src/view/components/OrderTypes/OrderTypes';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { productUtils } from '~/utils/productUtils';
import { ProductWarning } from '~/view/components/ProductWarning';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './style.scss';

interface IProps {
  item?: IProductV4;
  index?: number;
}

const ItemsListRow: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const [tooltip, setTooltip] = useState<boolean>(false);

  const editItem = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !props.item ||
      (event.target as HTMLDivElement).classList.contains('icon-warning') ||
      (event.target as HTMLDivElement).classList.contains('tooltip__overlay')
    ) {
      return;
    }

    history.push(SCREENS.ITEMS_EDIT(props.item.id));
  };

  const deleteItem = () => {
    confirmDelete({
      message: localeStore.t('confirm_deleting_item'),
      onConfirm: () => props.item && itemsStore.deleteItem(props.item.id),
      redConfirmBtn: true,
    });
  };

  const restoreItem = () => {
    if (!props.item || itemsStore.isLoading) return;
    itemsStore.restoreItem(props.item.id);
  };

  const changeStatus = useCallback(
    (status: TBooleanInNumber) => {
      if (!props.item) return;
      itemsStore.changeStatus(props.item.id, status);
    },
    [props.item]
  );

  const changeStock = useCallback(
    (in_stock: TBooleanInNumber) => {
      if (!props.item) return;
      itemsStore.changeStock(props.item.id, in_stock);
    },
    [props.item]
  );

  const renderSwitcher = useCallback(
    (status: TBooleanInNumber, onChange: (status: TBooleanInNumber) => void): JSX.Element | null => {
      if (!props.item) return <Skeleton width={50} height={30} style={{ borderRadius: 30 }} />;

      if (props.item.is_delete) return null;

      return <Switcher status={status} disabled={itemsStore.isLoading} onChange={onChange} />;
    },
    [itemsStore.isLoading, props.item]
  );

  const restoreWarning = useMemo((): string | undefined => {
    if (!props.item || !props.item.is_delete) return undefined;

    if (props.item.category && props.item.category.is_delete) {
      return localeStore.t('restore_cat_first_err', { category: getTranslate(props.item.category.name) });
    }

    return undefined;
  }, [props.item]);

  const orderTypeIconNames = getOrderTypeIconsNames(props.item?.order_type);

  const { item } = props;

  return (
    <li className={`items-list-row ${props.index ? 'mt-4' : ''}`}>
      {/* image and name */}
      <div
        onClick={(event) => (item?.is_delete ? undefined : editItem(event))}
        className={`d-flex flex-row align-items-center ${item?.is_delete ? 'w-70' : 'cursor-pointer w-25'}`}
      >
        <Image
          refreshing={!item}
          src={item?.image || ''}
          alt={getTranslate(item?.name) + '-image'}
          wrapperClassName="items-list-row__image mr-4"
          className="full-height"
          aspectRatio="none"
        />
        <div className="items-list-row__name-container items-list-row__border d-flex flex-column justify-content-center full-height pr-4">
          {item ? <Text type={TEXT.LIST_ROW} text={item.name} /> : <Skeleton width={125} height={19.2} />}
          {item?.is_donation ? <Text type={TEXT.DONATABLE_IN_LIST} text="donatable" /> : null}
        </div>
      </div>
      {item?.is_delete ? null : (
        <>
          {/* category */}
          <div onClick={editItem} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-15 pr-4">
            {item ? (
              <>
                <Text type={TEXT.LIST_ROW} text={item.category?.name} />
                <ProductWarning type="categoryInList" product={item} />
              </>
            ) : (
              <Skeleton width={100} height={19.2} />
            )}
          </div>
          {/* order type */}
          <div onClick={editItem} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-15">
            {item ? <OrderTypes types={orderTypeIconNames} /> : <Skeleton width={70} height={19.2} />}
          </div>
          {/* price */}
          <div onClick={editItem} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-15">
            {item ? (
              <Text type={TEXT.LIST_ROW} text={formatPrice(productUtils.getProductPriceInList(item))} noTranslate />
            ) : (
              <Skeleton width={70} height={19.2} />
            )}
          </div>
          {/* sort */}
          <div onClick={editItem} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-10">
            {item ? <Text type={TEXT.LIST_ROW} text={item.sort} /> : <Skeleton width={50} height={19.2} />}
          </div>
          {/* in_stock switcher */}
          <IntegrationManager
            setting={integrationsStore.posRestrictions?.globalItemStock}
            wrapperClassName="items-list-row__border d-flex flex-row align-items-center w-10"
            withTooltip
          >
            {renderSwitcher(booleanNumberReverse(item?.out_of_stock), changeStock)}
          </IntegrationManager>
          {/* availability switcher */}
          <IntegrationManager
            setting={integrationsStore.posRestrictions?.globalItemAvailability}
            wrapperClassName="items-list-row__border d-flex flex-row align-items-center w-5"
            tooltipOptions={{ minWidth: 355 }}
            withTooltip
          >
            {renderSwitcher(item?.status || 0, changeStatus)}
          </IntegrationManager>
        </>
      )}
      {/* delete/restore block */}
      <IntegrationManager
        setting={item?.is_delete ? integrationsStore.posRestrictions?.itemRestore : integrationsStore.posRestrictions?.itemDelete}
        wrapperClassName={`items-list-row__border d-flex flex-row align-items-center justify-content-end ${item?.is_delete ? 'w-30' : 'w-5'} `}
        tooltipOptions={{ minWidth: 355 }}
        withTooltip
      >
        {restoreWarning ? (
          <TooltipIcon
            message={restoreWarning}
            trigger={tooltip}
            disableTrigger={() => setTooltip(false)}
            duration={TOOLTIP_DURATION.EXTRA_LARGE}
            minWidth={330}
            maxWidth={330}
            iconClassName="mr-3"
          />
        ) : null}
        <DeleteRestoreBlock
          skeleton={!item}
          disabled={itemsStore.isLoading || Boolean(restoreWarning)}
          isDelete={item?.is_delete}
          hideTooltip={() => setTooltip(true)}
          onRestore={restoreItem}
          onDelete={deleteItem}
        />
      </IntegrationManager>
    </li>
  );
});

export default ItemsListRow;
